export type ChatbotDateValue = string | Date;

export type ChatbotInstanceStatus = 'ACTIVE' | 'INACTIVE';
export type ChatbotAccessType = 'ANONYMOUS' | 'WHITELIST' | 'DOMAIN' | 'OPEN';
export type ChatbotVerificationMethod = 'NONE' | 'EMAIL_OTP' | 'SMS_OTP';
export type ChatbotConversationMode = 'SINGLE' | 'MULTIPLE';
export type ChatbotBotKind = 'STANDARD' | 'CONCIERGE';
export type ChatbotConversationStatus = 'ACTIVE' | 'ARCHIVED';
export type ChatbotIdentifierType = 'EMAIL' | 'PHONE' | 'ANONYMOUS';
export type ChatbotContentType = 'TEXT' | 'AUDIO' | 'IMAGE' | 'FILE';
export type ChatbotMessageSender = 'USER' | 'BOT' | 'OPERATOR';
export type ChatbotHandoffStatus = 'BOT_ACTIVE' | 'HUMAN_ACTIVE' | 'CLOSED';
export type TenantStatus = 'ACTIVE' | 'INACTIVE';
export type ChatbotLimitReason = 'hourly' | 'daily' | 'total';
export type InstanceOutboundChannel = 'WHATSAPP' | 'SMS' | 'EMAIL';

export interface Tenant<TDate extends ChatbotDateValue = string> {
  id: string;
  name: string;
  slug: string | null;
  status: TenantStatus;
  config: Record<string, unknown> | null;
  createdAt: TDate;
  updatedAt: TDate;
  instancesCount?: number;
}

export interface CreateTenantRequest {
  name: string;
  slug?: string;
  status?: TenantStatus;
  config?: Record<string, unknown>;
}

export type UpdateTenantRequest = Partial<CreateTenantRequest>;

export interface ChatbotAccessConfig {
  sessionExpiryHours?: number;
  otpExpiryMinutes?: number;
  otpMaxAttempts?: number;
  allowedDomains?: string[];
  smsProvider?: string;
}

export interface ChatbotInputConfig {
  audio?: {
    enabled: boolean;
    maxDurationSeconds?: number;
  };
  images?: {
    enabled: boolean;
    maxSizeMb?: number;
  };
  files?: {
    enabled: boolean;
    allowedExtensions?: string[];
    maxSizeMb?: number;
  };
}

export interface ChatbotLimitsConfig {
  messagesPerHour?: number | null;
  messagesPerDay?: number | null;
  messagesTotal?: number | null;
}

export interface ChatbotInstance<TDate extends ChatbotDateValue = string> {
  id: string;
  tenantId: string;
  tenantName?: string;
  name: string;
  description: string | null;
  status: ChatbotInstanceStatus;
  accessType: ChatbotAccessType;
  verificationMethod: ChatbotVerificationMethod;
  otpEmailAccountId?: string | null;
  accessConfig: ChatbotAccessConfig | null;
  inputConfig: ChatbotInputConfig | null;
  limitsConfig: ChatbotLimitsConfig | null;
  enabledOutboundChannels?: InstanceOutboundChannel[];
  createdAt: TDate;
  updatedAt: TDate;
  botsCount?: number;
  concierge?: { botId: string; name: string; webMandatory: boolean } | null;
}

export type ChatbotAiProvider = 'openai' | 'anthropic' | 'openrouter' | 'local';

export const CHATBOT_INFO_FIELD_LIMITS = {
  maxFields: 5,
  labelMax: 64,
  valueMax: 500,
} as const;

export interface ChatbotInfoField {
  label: string;
  value: string;
}

export interface ChatbotConversationInfoFieldsResponse {
  enabled: boolean;
  infoFields: ChatbotInfoField[];
}

export interface PatchBotInfoFieldsRequest {
  infoFields: ChatbotInfoField[];
  botInfoFieldsEnabled?: boolean;
}

export function compactChatbotInfoFields(fields: unknown): ChatbotInfoField[] {
  if (!Array.isArray(fields)) {
    return [];
  }
  const bounded = fields.slice(0, 32);
  const out: ChatbotInfoField[] = [];
  for (const item of bounded) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const rec = item as Record<string, unknown>;
    const label = typeof rec.label === 'string' ? rec.label.slice(0, CHATBOT_INFO_FIELD_LIMITS.labelMax).trim() : '';
    const value = typeof rec.value === 'string' ? rec.value.slice(0, CHATBOT_INFO_FIELD_LIMITS.valueMax).trim() : '';
    if (!label && !value) {
      continue;
    }
    out.push({ label, value });
    if (out.length >= CHATBOT_INFO_FIELD_LIMITS.maxFields) {
      break;
    }
  }
  return out;
}

export function padChatbotInfoFields(fields: ChatbotInfoField[] | null | undefined): ChatbotInfoField[] {
  const compacted = compactChatbotInfoFields(fields);
  const padded = compacted.slice(0, CHATBOT_INFO_FIELD_LIMITS.maxFields);
  while (padded.length < CHATBOT_INFO_FIELD_LIMITS.maxFields) {
    padded.push({ label: '', value: '' });
  }
  return padded;
}

export type AiApiKeySource = 'bot' | 'tenant' | 'environment' | 'missing';

export interface AiApiKeyStatus {
  configured: boolean;
  source: AiApiKeySource;
  /** Key almacenada en el bot (columna dedicada o legacy del proveedor del bot). */
  storedOnBot: boolean;
}

export interface NoragBotConfigStatus {
  enabled: boolean;
  configured: boolean;
  storedTokenOnBot: boolean;
  autoProvisionAvailable: boolean;
  empresaId: number | null;
  botId: string | null;
  apiUrl: string | null;
}

export interface ChatbotConciergeConfig {
  botId: string;
  tenantId: string;
  webMandatory: boolean;
  idleMinutes: number;
  pendingIdleMinutes: number;
  welcomeText: string;
  escapeKeywords: string[];
  classifyEnabled: boolean;
  topicShiftEnabled: boolean;
}

export interface ChatbotConciergeInput {
  webMandatory: boolean;
  idleMinutes?: number;
  pendingIdleMinutes?: number;
  welcomeText: string;
  escapeKeywords?: string[];
  classifyEnabled?: boolean;
  topicShiftEnabled?: boolean;
}

export interface ChatbotTransferredTo {
  botId: string;
  botName: string;
  conversationId: string;
  openingMessage?: string | null;
}

export interface ChatbotBot<TDate extends ChatbotDateValue = string> {
  id: string;
  name: string;
  description: string | null;
  provider: ChatbotAiProvider;
  modelId: string | null;
  systemPrompt: string | null;
  temperature?: number | null;
  maxOutputTokens?: number | null;
  sttModelId: string;
  aiReady: boolean;
  openaiApiKey?: AiApiKeyStatus;
  anthropicApiKey?: AiApiKeyStatus;
  historyMessageLimit?: number | null;
  historyMessageMaxChars?: number | null;
  conversationMode: ChatbotConversationMode;
  iconUrl: string | null;
  kind?: ChatbotBotKind;
  concierge?: ChatbotConciergeConfig | null;
  norag?: NoragBotConfigStatus;
  botInfoFieldsEnabled?: boolean;
  userInfoFieldsEnabled?: boolean;
  infoFields?: ChatbotInfoField[];
  operatorIdleResumeMinutes?: number;
  operatorTakeoverNotice?: string | null;
  operatorResumeNotice?: string | null;
  createdAt: TDate;
}

export interface ChatbotBotInstance<TDate extends ChatbotDateValue = string> {
  id?: string;
  botId: string;
  instanceId: string;
  name: string;
  description: string | null;
  provider: ChatbotAiProvider;
  modelId: string | null;
  systemPrompt: string | null;
  temperature?: number | null;
  maxOutputTokens?: number | null;
  sttModelId: string;
  aiReady: boolean;
  openaiApiKey?: AiApiKeyStatus;
  anthropicApiKey?: AiApiKeyStatus;
  historyMessageLimit?: number | null;
  historyMessageMaxChars?: number | null;
  conversationMode: ChatbotConversationMode;
  iconUrl: string | null;
  kind?: ChatbotBotKind;
  concierge?: ChatbotConciergeConfig | null;
  displayOrder: number;
  isActive: boolean;
  menuLabel?: string | null;
  menuDescription?: string | null;
  isRoutable?: boolean;
  norag?: NoragBotConfigStatus;
  botInfoFieldsEnabled?: boolean;
  userInfoFieldsEnabled?: boolean;
  infoFields?: ChatbotInfoField[];
  createdAt: TDate;
  conversationsCount?: number;
}

export interface ChatbotBotDetail<TDate extends ChatbotDateValue = string> {
  bot: ChatbotBot<TDate>;
  instancesCount: number;
  linkedInstances: Array<{
    instanceId: string;
    name: string;
    displayOrder: number;
    isActive: boolean;
  }>;
  lastAiAuthErrorAt?: TDate | null;
  lastAiAuthErrorMessage?: string | null;
}

export interface ChatbotAiCatalog {
  models: Array<{
    id: string;
    label: string;
    provider: ChatbotAiProvider;
    modelId: string;
    description: string;
    supportsVision: boolean;
  }>;
  openrouter: Array<{
    id: string;
    label: string;
    vendor?: string;
    provider: 'openrouter';
  }>;
  openrouterConfigured: boolean;
  local: Array<{
    id: string;
    label: string;
    provider: 'local';
  }>;
  localConfigured: boolean;
  sttModels: Array<{ id: string; label: string }>;
  promptTemplates: Array<{
    id: string;
    name: string;
    description: string;
    suggestedModelId: string;
    systemPrompt: string;
    tags: string[];
  }>;
}

export interface ChatbotBotPublic {
  id: string;
  name: string;
  description: string | null;
  conversationMode: ChatbotConversationMode;
  iconUrl: string | null;
  kind?: ChatbotBotKind;
  conversationsCount?: number;
  menuLabel?: string | null;
  menuDescription?: string | null;
}

export interface ChatbotConversation<TDate extends ChatbotDateValue = string> {
  id: string;
  userId?: string;
  userIdentifier?: string;
  userIdentifierType?: ChatbotIdentifierType;
  userPhone?: string | null;
  userLabel?: string;
  botId: string;
  botName: string;
  tenantId?: string;
  tenantName?: string | null;
  aiThreadId: string;
  startedAt: TDate;
  lastMessageAt: TDate;
  messageCount: number;
  status?: ChatbotConversationStatus;
  lastMessagePreview?: string;
  userInfoFieldsEnabled?: boolean;
  channel?: 'WEB' | 'WHATSAPP' | 'HELPDESK' | 'CHATWOOT' | 'TELEGRAM' | 'FACEBOOK' | 'INSTAGRAM';
  handoffStatus?: ChatbotHandoffStatus;
  windowExpiresAt?: TDate | null;
  personId?: string | null;
}

export interface ChatbotFileMetadata {
  name?: string;
  size?: number;
  mimeType?: string;
  type?: string;
}

export interface ChatbotMessage<TDate extends ChatbotDateValue = string> {
  id: string;
  sender: ChatbotMessageSender;
  contentType: ChatbotContentType;
  content: string;
  /** Texto para contexto IA / exportación; no se muestra en el chat público */
  contextContent?: string | null;
  fileUrl: string | null;
  fileMetadata?: ChatbotFileMetadata | Record<string, unknown> | null;
  createdAt: TDate;
}

export interface ChatbotWhitelistEntry<TDate extends ChatbotDateValue = string> {
  id: string;
  email: string;
  addedAt: TDate;
}

export interface CreateInstanceRequest {
  tenantId: string;
  name: string;
  description?: string;
  status?: ChatbotInstanceStatus;
  accessType?: ChatbotAccessType;
  verificationMethod?: ChatbotVerificationMethod;
  accessConfig?: ChatbotAccessConfig;
  inputConfig?: ChatbotInputConfig;
  limitsConfig?: ChatbotLimitsConfig;
}

export interface UpdateInstanceRequest {
  name?: string;
  description?: string;
  status?: ChatbotInstanceStatus;
  accessType?: ChatbotAccessType;
  verificationMethod?: ChatbotVerificationMethod;
  accessConfig?: ChatbotAccessConfig;
  inputConfig?: ChatbotInputConfig;
  limitsConfig?: ChatbotLimitsConfig;
  enabledOutboundChannels?: InstanceOutboundChannel[];
  otpEmailAccountId?: string | null;
}

export interface CreateBotRequest {
  name: string;
  description?: string;
  provider?: ChatbotAiProvider;
  modelId?: string;
  systemPrompt?: string;
  kind?: ChatbotBotKind;
  concierge?: ChatbotConciergeInput;
  /** @deprecated Usar openaiApiKey / anthropicApiKey */
  apiKey?: string;
  openaiApiKey?: string;
  anthropicApiKey?: string;
  temperature?: number;
  maxOutputTokens?: number;
  sttModelId?: string;
  historyMessageLimit?: number;
  historyMessageMaxChars?: number;
  conversationMode?: ChatbotConversationMode;
  iconUrl?: string;
  noragEnabled?: boolean;
  noragEmpresaId?: number | null;
  noragBotId?: string | null;
  noragApiUrl?: string | null;
  noragPathPrefix?: string | null;
  noragBotToken?: string;
  botInfoFieldsEnabled?: boolean;
  userInfoFieldsEnabled?: boolean;
  infoFields?: ChatbotInfoField[];
}

export interface UpdateBotRequest {
  name?: string;
  description?: string;
  provider?: ChatbotAiProvider;
  modelId?: string;
  systemPrompt?: string;
  kind?: ChatbotBotKind;
  concierge?: ChatbotConciergeInput;
  /** @deprecated Usar openaiApiKey / anthropicApiKey */
  apiKey?: string;
  openaiApiKey?: string;
  anthropicApiKey?: string;
  clearOpenaiApiKey?: boolean;
  clearAnthropicApiKey?: boolean;
  temperature?: number;
  maxOutputTokens?: number;
  sttModelId?: string;
  historyMessageLimit?: number;
  historyMessageMaxChars?: number;
  conversationMode?: ChatbotConversationMode;
  iconUrl?: string;
  noragEnabled?: boolean;
  noragEmpresaId?: number | null;
  noragBotId?: string | null;
  noragApiUrl?: string | null;
  noragPathPrefix?: string | null;
  noragBotToken?: string;
  clearNoragBotToken?: boolean;
  botInfoFieldsEnabled?: boolean;
  userInfoFieldsEnabled?: boolean;
  infoFields?: ChatbotInfoField[];
  operatorIdleResumeMinutes?: number;
  operatorTakeoverNotice?: string | null;
  operatorResumeNotice?: string | null;
}

export interface OpenRouterCatalogEntry {
  id: string;
  label: string;
  vendor: string;
  description: string;
  enabled: boolean;
}

export interface TenantAiKeysResponse {
  tenantId: string;
  tenantName: string;
  openaiApiKey: AiApiKeyStatus;
  anthropicApiKey: AiApiKeyStatus;
  openrouterApiKey: AiApiKeyStatus;
  localLlmApiKey: AiApiKeyStatus;
  openrouterEnabledModels: string[];
  openrouterCatalog: OpenRouterCatalogEntry[];
  openrouterConfigured: boolean;
  localLlmBaseUrl: string | null;
  localLlmEnabledModels: string[];
  localConfigured: boolean;
}

export interface UpdateTenantAiKeysRequest {
  tenantId?: string;
  openaiApiKey?: string;
  anthropicApiKey?: string;
  clearOpenaiApiKey?: boolean;
  clearAnthropicApiKey?: boolean;
  openrouterApiKey?: string;
  clearOpenrouterApiKey?: boolean;
  openrouterEnabledModels?: string[];
  localLlmBaseUrl?: string | null;
  localLlmEnabledModels?: string[];
  localLlmApiKey?: string;
  clearLocalLlmApiKey?: boolean;
}

export interface AddBotToInstanceRequest {
  botId: string;
  displayOrder?: number;
  isActive?: boolean;
  menuLabel?: string | null;
  menuDescription?: string | null;
  isRoutable?: boolean;
}

export interface UpdateBotOnInstanceRequest {
  displayOrder?: number;
  isActive?: boolean;
  menuLabel?: string | null;
  menuDescription?: string | null;
  isRoutable?: boolean;
}

export interface AddWhitelistEmailRequest {
  email: string;
}

export interface RequestOtpRequest {
  email?: string;
  phone?: string;
}

export interface VerifyOtpRequest {
  email?: string;
  phone?: string;
  code: string;
}

export interface ChatbotUpload {
  data: string;
  type: 'file' | 'file:full' | 'audio' | 'image' | 'url';
  name: string;
  mime: string;
}

/** @deprecated Use ChatbotUpload */
export type FlowiseUpload = ChatbotUpload;

export interface ChatbotAttributionContext {
  source?: string;
  medium?: string;
  funnelRoute?: string;
  campaignId?: string;
  campaignName?: string;
  groupId?: string;
  groupName?: string;
  adId?: string;
  adName?: string;
  formId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  gclid?: string;
  fbclid?: string;
  ctwaClid?: string;
  landingUrl?: string;
  referrer?: string;
}

export interface SendMessageRequest {
  content: string;
  contentType?: ChatbotContentType;
  streaming?: boolean;
  fileUrl?: string;
  fileMetadata?: ChatbotFileMetadata;
  uploads?: ChatbotUpload[];
  attribution?: ChatbotAttributionContext;
}

export interface AiChatRequest {
  question: string;
  sessionId: string;
  streaming?: boolean;
  uploads?: ChatbotUpload[];
  contextMetadata?: Record<string, unknown>;
}

export interface AiChatResponse {
  text: string;
  sessionId?: string;
}

/** @deprecated Use AiChatRequest */
export type FlowisePredictionRequest = AiChatRequest & {
  overrideConfig?: Record<string, unknown>;
};

/** @deprecated Use AiChatResponse */
export type FlowisePredictionResponse = AiChatResponse & {
  chatId?: string;
  sourceDocuments?: unknown[];
  usedTools?: unknown[];
};

export interface OtpResponse<TDate extends ChatbotDateValue = string> {
  success: boolean;
  message?: string;
  expiresAt?: TDate;
  otpRequired?: boolean;
  phoneRequired?: boolean;
}

export interface SendMessageResponse<TDate extends ChatbotDateValue = string> {
  userMessage: ChatbotMessage<TDate>;
  botMessage: ChatbotMessage<TDate>;
  transferredTo?: ChatbotTransferredTo;
}

export interface ChatbotAuthState {
  isAuthenticated: boolean;
  instanceId: string | null;
  accessType: ChatbotAccessType | null;
  identifier: string | null;
}

export interface ChatbotPublicConfig {
  instanceId: string;
  name: string;
  accessType: ChatbotAccessType;
  verificationMethod: ChatbotVerificationMethod;
  allowedDomains: string[];
  inputConfig?: ChatbotInputConfig;
  limitsConfig?: ChatbotLimitsConfig;
  bots: Array<{
    id: string;
    name: string;
    description: string | null;
    kind?: ChatbotBotKind;
  }>;
  concierge?: { webMandatory: boolean } | null;
}

export interface ChatbotMetrics {
  totalConversations: number;
  totalMessages: number;
  avgResponseTime: number;
  limitViolations: number;
  bots: Array<{
    id: string;
    name: string;
    conversations: number;
    messages: number;
  }>;
}

export interface ChatbotLimitState<TDate extends ChatbotDateValue = string> {
  reached: boolean;
  reason?: ChatbotLimitReason;
  resetAt?: TDate;
}

export interface ChatbotSessionData<TDate extends ChatbotDateValue = Date> {
  userId: string;
  instanceId: string;
  identifier: string;
  identifierType: ChatbotIdentifierType;
  expiresAt: TDate;
}

export interface ChatbotAuthResult<TDate extends ChatbotDateValue = Date> {
  success: boolean;
  message?: string;
  session?: ChatbotSessionData<TDate>;
}

export type ChatbotOtpFailureCode = 'RATE_LIMITED' | 'NOT_CONFIGURED' | 'SEND_FAILED';

export interface ChatbotOtpResult<TDate extends ChatbotDateValue = Date> {
  success: boolean;
  message?: string;
  expiresAt?: TDate;
  /** Razón de fallo cuando success=false, para que el caller decida HTTP status. */
  failureCode?: ChatbotOtpFailureCode;
}

export interface ChatbotLimitCheckResult<TDate extends ChatbotDateValue = Date> {
  allowed: boolean;
  reason?: ChatbotLimitReason;
  resetAt?: TDate;
  remaining?: number;
}

export interface AiChatError {
  message: string;
  statusCode?: number;
  retryable: boolean;
}

/** @deprecated Use AiChatError */
export type FlowiseError = AiChatError;

export interface AccessValidationResult {
  valid: boolean;
  reason?: string;
  requiresOtp?: boolean;
  phoneRequired?: boolean;
}

export type CreateChatbotInstanceDto = CreateInstanceRequest;
export type UpdateChatbotInstanceDto = UpdateInstanceRequest;
export type CreateChatbotBotDto = CreateBotRequest;
export type UpdateChatbotBotDto = UpdateBotRequest;
export type AddBotToInstanceDto = AddBotToInstanceRequest;
export type UpdateBotOnInstanceDto = UpdateBotOnInstanceRequest;
export type AddWhitelistEmailDto = AddWhitelistEmailRequest;
export type RequestOtpDto = RequestOtpRequest;
export type VerifyOtpDto = VerifyOtpRequest;

export type ChatbotInstanceResponse = ChatbotInstance<Date>;
export type ChatbotBotResponse = ChatbotBot<Date>;
export type ChatbotBotInstanceResponse = Required<Pick<ChatbotBotInstance<Date>, 'id'>> & ChatbotBotInstance<Date>;
export type ChatbotBotPublicResponse = ChatbotBotPublic;
export type ChatbotConversationResponse = ChatbotConversation<Date>;
export type ChatbotMessageResponse = ChatbotMessage<Date>;
export type ChatbotWhitelistResponse = ChatbotWhitelistEntry<Date>;

export interface ChatbotInstanceBotRelation {
  id: string;
  instanceId: string;
  botId: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  bot?: ChatbotBotResponse;
}
